import { useCallback, useReducer } from 'react'

import { IFilterOption } from '../types'

// @i18n-ignore-start
export const SORT_BY = {
  HIGHEST_RATING: 'HIGHEST_RATING',
  LOWEST_RATING: 'LOWEST_RATING',
  NEWEST: 'NEWEST',
  OLDEST: 'OLDEST',
  DEFAULT: '',
} as const

export const SORT_BY_OPTIONS_MAP = {
  [SORT_BY.HIGHEST_RATING]: { sortBy: 'rating', sortOrder: 'DESC' },
  [SORT_BY.NEWEST]: { sortBy: 'createdDate', sortOrder: 'DESC' },
  [SORT_BY.LOWEST_RATING]: { sortBy: 'rating', sortOrder: 'ASC' },
  [SORT_BY.OLDEST]: { sortBy: 'createdDate', sortOrder: 'ASC' },
  [SORT_BY.DEFAULT]: { sortBy: 'rating', sortOrder: 'DESC' },
}
// @i18n-ignore-end

export const DEFAULT_FILTER_BY_OPTION = { key: 'TAGS', value: 'All', count: 0, displayName: 'All' }

type SORT_BY_KEYS = keyof typeof SORT_BY
type SORT_BY_TYPES = (typeof SORT_BY)[SORT_BY_KEYS]

interface FilterState {
  page: number
  sortBy: SORT_BY_TYPES
  hasPhoto: boolean
  filterBy: IFilterOption
}
enum ACTION_TYPES {
  SET_PAGE = 'SET_PAGE',
  SET_SORT = 'SET_SORT',
  SET_HAS_PHOTOS = 'SET_HAS_PHOTOS',
  SET_FILTER_BY = 'SET_FILTER_BY',
}

type Action =
  | {
      type: ACTION_TYPES.SET_HAS_PHOTOS
      hasPhoto: boolean
    }
  | {
      type: ACTION_TYPES.SET_SORT
      sortBy: SORT_BY_TYPES
    }
  | {
      type: ACTION_TYPES.SET_PAGE
      page: number
    }
  | {
      type: ACTION_TYPES.SET_FILTER_BY
      filterBy: IFilterOption
    }
const reducer = (state: FilterState, action: Action) => {
  const nextState = { ...state }
  switch (action.type) {
    case ACTION_TYPES.SET_HAS_PHOTOS:
      nextState.hasPhoto = action.hasPhoto
      nextState.page = 1
      break
    case ACTION_TYPES.SET_PAGE:
      nextState.page = action.page
      break
    case ACTION_TYPES.SET_SORT:
      nextState.sortBy = action.sortBy
      nextState.page = 1
      break
    case ACTION_TYPES.SET_FILTER_BY:
      nextState.filterBy = action.filterBy
      break
  }
  return nextState
}

const DEFAULT_FILTER_STATE = {
  page: 1,
  sortBy: SORT_BY.DEFAULT,
  filterBy: DEFAULT_FILTER_BY_OPTION,
  hasPhoto: false,
}
function useFilterState() {
  const [{ hasPhoto, page, sortBy, filterBy }, dispatch] = useReducer(reducer, DEFAULT_FILTER_STATE)

  const setHasPhoto = useCallback(
    (hasPhoto: boolean) => {
      dispatch({
        type: ACTION_TYPES.SET_HAS_PHOTOS,
        hasPhoto,
      })
    },
    [dispatch],
  )
  const setSortBy = useCallback(
    (sortBy: string) => {
      dispatch({
        type: ACTION_TYPES.SET_SORT,
        sortBy: sortBy as SORT_BY_TYPES,
      })
    },
    [dispatch],
  )

  const setPage = useCallback(
    (page: number) => {
      dispatch({
        type: ACTION_TYPES.SET_PAGE,
        page,
      })
    },
    [dispatch],
  )

  const setFilterBy = useCallback(
    (filterBy: IFilterOption) => {
      dispatch({
        type: ACTION_TYPES.SET_FILTER_BY,
        filterBy,
      })
    },
    [dispatch],
  )

  return {
    setHasPhoto,
    setSortBy,
    setPage,
    hasPhoto,
    page,
    sortBy,
    filterBy,
    setFilterBy,
  }
}

export default useFilterState
